import {
  CreateQuizQuestionPayload,
  ManualGradeAttemptPayload,
  Quiz,
  QuizAttempt,
  QuizAttemptAnswerMap,
  QuizAttemptAnswersPayload,
  QuizAttemptFile,
  QuizAttemptMeta,
  QuizAttemptScore,
  QuizQuestion,
  QuizQuestionMetadata,
  QuizQuestionType,
  UpdateQuizQuestionPayload,
} from '@/types/quizzes'

type RawRecord = Record<string, any>

const parseNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const num = typeof value === 'number' ? value : Number(value)
  return Number.isNaN(num) ? null : num
}

const parseBoolean = (value: unknown): boolean | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    const normalized = value.toLowerCase()
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true
    if (['false', '0', 'no', 'n'].includes(normalized)) return false
  }
  if (typeof value === 'number') {
    if (value === 1) return true
    if (value === 0) return false
  }
  return null
}

const toNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  const text = String(value).trim()
  return text.length ? text : null
}

const mapOptions = (raw: any): { key: string; text: string }[] => {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((item) => item)
    .map((item) => ({
      key: String(item.key ?? item.value ?? item.id ?? '').trim(),
      text: String(item.text ?? item.label ?? '').trim(),
    }))
    .filter((option) => option.key && option.text)
}

const mapMetadata = (raw: any): QuizQuestionMetadata => {
  if (!raw || typeof raw !== 'object') return raw ?? null
  return { ...raw }
}

export const mapQuizQuestion = (json: RawRecord): QuizQuestion => {
  const type = String(json.type ?? 'mcq').toLowerCase() as QuizQuestionType
  const base = {
    id: String(json.id),
    quizId: String(json.quiz_id ?? json.quizId ?? ''),
    order: parseNumber(json.order) ?? 0,
    questionText: String(json.question_text ?? json.questionText ?? ''),
    scoreWeight: parseNumber(json.score_weight ?? json.scoreWeight) ?? 0,
    metadata: mapMetadata(json.metadata),
    createdBy: json.created_by ?? json.createdBy ?? null,
    createdAt: json.created_at ?? json.createdAt ?? null,
    updatedAt: json.updated_at ?? json.updatedAt ?? null,
  }

  if (type === 'mcq') {
    return {
      ...base,
      type,
      options: mapOptions(json.options),
      correctAnswer: json.correct_answer ?? json.correctAnswer ?? null,
    }
  }

  if (type === 'multi') {
    const correct = json.correct_answer ?? json.correctAnswer
    const normalized = Array.isArray(correct) ? correct.map(String) : []
    return {
      ...base,
      type,
      options: mapOptions(json.options),
      correctAnswer: normalized,
    }
  }

  if (type === 'numeric') {
    const correct = json.correct_answer ?? json.correctAnswer ?? null
    return {
      ...base,
      type,
      options: null,
      correctAnswer: correct
        ? {
            value: parseNumber(correct.value) ?? 0,
            tolerance: parseNumber(correct.tolerance),
          }
        : null,
    }
  }

  if (type === 'short_answer') {
    return {
      ...base,
      type,
      options: null,
      correctAnswer: json.correct_answer ?? json.correctAnswer ?? null,
      metadata: mapMetadata(json.metadata),
    }
  }

  if (type === 'file_upload') {
    return {
      ...base,
      type,
      options: null,
      correctAnswer: null,
      metadata: mapMetadata(json.metadata),
    }
  }

  return {
    ...base,
    type,
    options: mapOptions(json.options),
    correctAnswer: json.correct_answer ?? json.correctAnswer ?? null,
  } as QuizQuestion
}

const parseScoreValue = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const normalized = value.replace(/[^\d.-]/g, '')
    const numeric = Number(normalized)
    return Number.isNaN(numeric) ? null : numeric
  }
  return null
}

const parseStringArray = (value: unknown): string[] | null => {
  if (!value) return null
  if (Array.isArray(value)) {
    return value.map((item) => String(item))
  }
  return null
}

const mapAttemptMeta = (raw?: any): QuizAttemptMeta | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const meta: QuizAttemptMeta = {}
  Object.entries(raw).forEach(([key, value]) => {
    if (value === undefined) return
    meta[key] = value as unknown
  })
  return meta
}

const mapAttemptLearner = (raw?: any): QuizAttempt['learner'] | null => {
  if (!raw) return null
  if (typeof raw === 'string') {
    const label = toNullableString(raw)
    if (!label) return null
    return {
      id: null,
      firstName: label,
      lastName: null,
      email: null,
    }
  }

  if (typeof raw !== 'object') return null

  const id =
    raw.id ?? raw.user_id ?? raw.userId ?? raw.learner_id ?? raw.learnerId ?? raw.uuid ?? null
  const firstName =
    raw.firstName ??
    raw.first_name ??
    raw.given_name ??
    raw.name ??
    raw.fullName ??
    raw.full_name ??
    null
  const lastName = raw.lastName ?? raw.last_name ?? raw.family_name ?? null
  const email = raw.email ?? raw.email_address ?? raw.contact_email ?? null

  const normalizedId = toNullableString(id)
  const normalizedFirst = toNullableString(firstName)
  const normalizedLast = toNullableString(lastName)
  const normalizedEmail = toNullableString(email)

  if (!normalizedId && !normalizedFirst && !normalizedLast && !normalizedEmail) {
    return null
  }

  return {
    id: normalizedId,
    firstName: normalizedFirst,
    lastName: normalizedLast,
    email: normalizedEmail,
  }
}

const extractAttemptLearner = (json: RawRecord): QuizAttempt['learner'] | null => {
  const nested =
    mapAttemptLearner(json.learner) ??
    mapAttemptLearner(json.learner_info) ??
    mapAttemptLearner(json.learnerProfile) ??
    mapAttemptLearner(json.user)

  if (nested) {
    const fallbackId = toNullableString(json.learner_id ?? json.learnerId) ?? nested.id ?? null
    return {
      ...nested,
      id: fallbackId,
    }
  }

  return mapAttemptLearner({
    id: json.learner_id ?? json.learnerId ?? null,
    firstName:
      json.learner_first_name ??
      json.learnerFirstName ??
      json.learner_name ??
      json.learnerName ??
      null,
    lastName: json.learner_last_name ?? json.learnerLastName ?? null,
    email: json.learner_email ?? json.learnerEmail ?? null,
  })
}

const mapAnswer = (raw: RawRecord): QuizAttemptAnswerMap => {
  const type = String(raw.type ?? raw.question_type ?? 'mcq').toLowerCase() as QuizQuestionType
  const base = {
    id: String(raw.id ?? raw.question_id ?? raw.questionId ?? ''),
    type,
  }
  const answerPayload = raw.answer ?? raw.response ?? null

  if (!answerPayload) {
    return { ...base, answer: null }
  }

  if (type === 'mcq' || type === 'multi') {
    const selected = parseStringArray(answerPayload.selected) ?? []
    return { ...base, answer: { selected } }
  }

  if (type === 'short_answer') {
    return { ...base, answer: { text: String(answerPayload.text ?? '') } }
  }

  if (type === 'numeric') {
    const numericValue = parseNumber(answerPayload.value) ?? 0
    return { ...base, answer: { value: numericValue } }
  }

  if (type === 'file_upload') {
    const files = Array.isArray(answerPayload.files)
      ? answerPayload.files.map((file: any) => ({
          filename: String(file.filename ?? file.name ?? ''),
          url: file.url ?? file.path ?? undefined,
        }))
      : []
    return { ...base, answer: { files } }
  }

  return { ...base, answer: answerPayload }
}

export const mapAttemptAnswers = (raw: RawRecord | null | undefined): QuizAttemptAnswersPayload | null => {
  if (!raw) return null
  const questionsRaw = Array.isArray(raw.questions) ? raw.questions : []
  return {
    quizId: String(raw.quizId ?? raw.quiz_id ?? ''),
    attempt: parseNumber(raw.attempt) ?? 1,
    startedAt: raw.startedAt ?? raw.started_at ?? new Date().toISOString(),
    submittedAt: raw.submittedAt ?? raw.submitted_at ?? null,
    durationSeconds: parseNumber(raw.durationSeconds ?? raw.duration_seconds),
    questions: questionsRaw.map((item: any) => mapAnswer(item)),
    meta: mapAttemptMeta(raw.meta),
  }
}

const mapAttemptFile = (raw: RawRecord): QuizAttemptFile => ({
  id: String(raw.id ?? raw.fileId ?? ''),
  attemptId: String(raw.attempt_id ?? raw.attemptId ?? ''),
  fileName: String(raw.file_name ?? raw.fileName ?? raw.filename ?? 'file'),
  url: String(raw.url ?? raw.path ?? ''),
  storageKey: raw.storage_key ?? raw.storageKey ?? null,
  size: parseNumber(raw.size ?? raw.file_size ?? raw.fileSize),
  mimeType: raw.mime_type ?? raw.mimeType ?? null,
  uploadedAt: raw.uploaded_at ?? raw.uploadedAt ?? null,
  createdAt: raw.created_at ?? raw.createdAt ?? null,
  updatedAt: raw.updated_at ?? raw.updatedAt ?? null,
})

const mapAttemptScore = (raw: RawRecord): QuizAttemptScore => ({
  id: String(raw.id ?? raw.scoreId ?? ''),
  attemptId: String(raw.attempt_id ?? raw.attemptId ?? ''),
  questionId: String(raw.question_id ?? raw.questionId ?? ''),
  autoScore: parseScoreValue(raw.auto_score ?? raw.autoScore),
  manualScore: parseScoreValue(raw.manual_score ?? raw.manualScore),
  feedback: raw.feedback ?? null,
  createdAt: raw.created_at ?? raw.createdAt ?? null,
  updatedAt: raw.updated_at ?? raw.updatedAt ?? null,
})

export class QuizModel implements Quiz {
  id: string
  courseId: string
  title: string
  slug?: string | null
  description?: string | null
  timeLimitSeconds?: number | null
  maxAttempts?: number | null
  isActive: boolean
  allowDevices?: string[] | null
  requireProctoring?: boolean | null
  dueDate?: string | null
  createdBy?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  questions?: QuizQuestion[]

  constructor(data: Quiz) {
    this.id = data.id
    this.courseId = data.courseId
    this.title = data.title
    this.slug = data.slug ?? null
    this.description = data.description ?? null
    this.timeLimitSeconds = data.timeLimitSeconds ?? null
    this.maxAttempts = data.maxAttempts ?? null
    this.isActive = data.isActive
    this.allowDevices = data.allowDevices ?? null
    this.requireProctoring = data.requireProctoring ?? null
    this.dueDate = data.dueDate ?? null
    this.createdBy = data.createdBy ?? null
    this.createdAt = data.createdAt ?? null
    this.updatedAt = data.updatedAt ?? null
    this.questions = data.questions
  }

  static fromJSON(json: RawRecord): QuizModel {
    const allowDevices = json.allowDevices ?? json.allow_devices ?? null
    return new QuizModel({
      id: String(json.id),
      courseId: String(json.course_id ?? json.courseId ?? ''),
      title: String(json.title ?? ''),
      slug: json.slug ?? null,
      description: json.description ?? null,
      timeLimitSeconds: parseNumber(json.time_limit_seconds ?? json.timeLimitSeconds),
      maxAttempts: parseNumber(json.max_attempts ?? json.maxAttempts),
      isActive: Boolean(json.is_active ?? json.isActive ?? true),
      allowDevices: Array.isArray(allowDevices) ? allowDevices.map(String) : null,
      requireProctoring: parseBoolean(json.require_proctoring ?? json.requireProctoring) ?? null,
      dueDate: json.due_date ?? json.dueDate ?? null,
      createdBy: json.created_by ?? json.createdBy ?? null,
      createdAt: json.created_at ?? json.createdAt ?? null,
      updatedAt: json.updated_at ?? json.updatedAt ?? null,
      questions: Array.isArray(json.questions)
        ? json.questions.map((question: RawRecord) => mapQuizQuestion(question))
        : undefined,
    })
  }
}

export class QuizAttemptModel implements QuizAttempt {
  id: string
  quizId: string
  learnerId: string
  learner?: QuizAttempt['learner'] | null
  attempt: number
  status: QuizAttempt['status']
  startedAt: string
  submittedAt?: string | null
  durationSeconds?: number | null
  autoScore?: number | null
  manualScore?: number | null
  answers?: QuizAttemptAnswersPayload | null
  ipAddress?: string | null
  userAgent?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  files?: QuizAttemptFile[]
  scores?: QuizAttemptScore[]

  constructor(data: QuizAttempt) {
    this.id = data.id
    this.quizId = data.quizId
    this.learnerId = data.learnerId
    this.learner = data.learner ?? null
    this.attempt = data.attempt
    this.status = data.status
    this.startedAt = data.startedAt
    this.submittedAt = data.submittedAt ?? null
    this.durationSeconds = data.durationSeconds ?? null
    this.autoScore = data.autoScore ?? null
    this.manualScore = data.manualScore ?? null
    this.answers = data.answers ?? null
    this.ipAddress = data.ipAddress ?? null
    this.userAgent = data.userAgent ?? null
    this.createdAt = data.createdAt ?? null
    this.updatedAt = data.updatedAt ?? null
    this.files = data.files ?? []
    this.scores = data.scores ?? []
  }

  static fromJSON(json: RawRecord): QuizAttemptModel {
    return new QuizAttemptModel({
      id: String(json.id),
      quizId: String(json.quiz_id ?? json.quizId ?? ''),
      learnerId: String(json.learner_id ?? json.learnerId ?? ''),
      attempt: parseNumber(json.attempt) ?? 1,
      status: (json.status ?? 'submitted') as QuizAttempt['status'],
      startedAt: json.started_at ?? json.startedAt ?? new Date().toISOString(),
      submittedAt: json.submitted_at ?? json.submittedAt ?? null,
      durationSeconds: parseNumber(json.duration_seconds ?? json.durationSeconds),
      autoScore: parseScoreValue(json.auto_score ?? json.autoScore),
      manualScore: parseScoreValue(json.manual_score ?? json.manualScore),
      answers: mapAttemptAnswers(json.answers),
      learner: extractAttemptLearner(json),
      ipAddress: json.ip_address ?? json.ipAddress ?? null,
      userAgent: json.user_agent ?? json.userAgent ?? null,
      createdAt: json.created_at ?? json.createdAt ?? null,
      updatedAt: json.updated_at ?? json.updatedAt ?? null,
      files: Array.isArray(json.files) ? json.files.map((file: RawRecord) => mapAttemptFile(file)) : [],
      scores: Array.isArray(json.scores) ? json.scores.map((score: RawRecord) => mapAttemptScore(score)) : [],
    })
  }

  get learnerName(): string | null {
    if (!this.learner) return null
    const first = this.learner.firstName?.trim()
    const last = this.learner.lastName?.trim()
    const combined = [first, last].filter(Boolean).join(' ').trim()
    if (combined) return combined
    return this.learner.email ?? null
  }
}

export const mapQuizList = (payload: unknown): QuizModel[] => {
  if (!payload) return []

  const toModels = (items: any[]) => items.map((item) => QuizModel.fromJSON(item as RawRecord))

  if (Array.isArray(payload)) {
    return toModels(payload)
  }

  const value = payload as RawRecord
  if (Array.isArray(value.data)) {
    return toModels(value.data)
  }

  if (value.data && typeof value.data === 'object') {
    const nested = value.data as RawRecord
    if (Array.isArray(nested.data)) {
      return toModels(nested.data)
    }
    if (Array.isArray(nested.quizzes)) {
      return toModels(nested.quizzes)
    }
  }

  if (Array.isArray(value.quizzes)) {
    return toModels(value.quizzes)
  }

  return []
}

export const mapAttemptList = (payload: unknown): QuizAttemptModel[] => {
  if (!payload) return []

  const toModels = (items: any[]) =>
    items.map((item) => QuizAttemptModel.fromJSON(item as RawRecord))

  if (Array.isArray(payload)) {
    return toModels(payload)
  }

  const value = payload as RawRecord
  if (Array.isArray(value.data)) {
    return toModels(value.data)
  }

  if (value.data && typeof value.data === 'object') {
    const nested = value.data as RawRecord
    if (Array.isArray(nested.data)) {
      return toModels(nested.data)
    }
    if (Array.isArray(nested.attempts)) {
      return toModels(nested.attempts)
    }
  }

  if (Array.isArray(value.attempts)) {
    return toModels(value.attempts)
  }

  return []
}

export const normalizeQuizQueryParams = (params?: unknown): URLSearchParams => {
  const search = new URLSearchParams()
  if (!params || typeof params !== 'object') return search
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return
    search.set(key, String(value))
  })
  return search
}

export const sanitizeQuestionPayload = <T extends CreateQuizQuestionPayload | UpdateQuizQuestionPayload>(
  payload: T,
): T => {
  const base: any = { ...payload }

  if ('options' in base && Array.isArray(base.options)) {
    base.options = base.options
      .map((option: any) => ({
        key: String(option.key ?? '').trim(),
        text: String(option.text ?? '').trim(),
      }))
      .filter((option: any) => option.key && option.text)
  }

  if ('correctAnswer' in base) {
    if (base.type === 'multi') {
      base.correctAnswer = Array.isArray(base.correctAnswer)
        ? base.correctAnswer.map((item: any) => String(item))
        : []
    }

    if (base.type === 'mcq' && typeof base.correctAnswer === 'string') {
      base.correctAnswer = base.correctAnswer.trim()
    }
  }

  return base as T
}

export const sanitizeManualGradePayload = (
  payload: ManualGradeAttemptPayload,
): ManualGradeAttemptPayload => ({
  ...payload,
  manualScores: payload.manualScores.map((entry) => ({
    questionId: String(entry.questionId),
    manualScore: Number(entry.manualScore),
    feedback: entry.feedback ?? undefined,
  })),
})
